import { useEffect, useState } from 'react'
import { Input, InputPassword } from '@/shared/ui/atoms/input'
import { Button } from '@/shared/ui/atoms/button'
import { Toast } from '@/shared/ui/organisms/toast'
import { SigninWrapper } from './signin-style'
import { ExclamationCircleIcon, LoginIcon, PasswordIcon } from '@/shared/ui/icons'
import { login } from '@/shared/api/auth'
import { hardResetSession } from '@/shared/api/session-reset'
import { getCookie } from '@/shared/lib/formatter'
import { useTranslation } from 'react-i18next'
import { useSafeNavigate } from '@/shared/lib/safe-navigate'
import dayjs from 'dayjs'

export const Signin = () => {
  const { t } = useTranslation()
  const navigate = useSafeNavigate()
  const [localState, setLocalState] = useState({
    phone: '',
    password: '',
  })
  const [errorMessage, setErrorMessage] = useState<Record<string, string | null>>({})
  const [isPending, setIsPending] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (name === 'phone') {
      const digits = value?.replace(/\D/g, '')
      const limitedDigits = digits?.slice(0, 9)
      setLocalState((prev) => ({ ...prev, phone: limitedDigits }))
      setErrorMessage((prev) => ({ ...prev, phone: null }))
    } else {
      setLocalState((prev) => ({ ...prev, [name]: value }))
      setErrorMessage((prev) => ({ ...prev, [name]: null }))
    }
  }

  // Already-authenticated bounce: if a valid token cookie is present and
  // not yet expired, send the user to home. <AuthBootstrap> at app root
  // already runs the /me hydration, so we only need the navigation gate
  // here. Invalid / expired tokens fall through to the login form;
  // bootstrapAuth will return false on the next page load and lets
  // routing handle it.
  useEffect(() => {
    const tokenCookie = getCookie('token')
    const expiresAt = getCookie('expiresAt')
    if (tokenCookie && expiresAt && dayjs(expiresAt).isAfter(dayjs())) {
      navigate('/')
    }
  }, [navigate])

  const handleSubmit = async () => {
    if (!localState.phone || !localState.password) {
      setErrorMessage((prev) => ({
        ...prev,
        phone: !localState.phone ? t('please_fill_the_fields') : (prev.phone ?? null),
        password: !localState.password ? t('please_fill_the_fields') : (prev.password ?? null),
      }))
      return
    }

    setIsPending(true)
    try {
      // Defensive clean slate — purge any residual session state before
      // establishing the new one. Redundant when the user landed here
      // via the 401 interceptor (which already runs hardResetSession
      // before redirecting), but cheap insurance for direct-URL entries,
      // new-tab logins, or any path that bypassed the interceptor and
      // left stale state behind. Single-flight, so this collapses with
      // any in-flight reset from the bounce path.
      await hardResetSession()

      // login() handles: token cookie set, tokenMemory mirror, /me hydration
      // into Zustand, broadcastLogin to other tabs. Returns the user payload.
      await login('+998' + localState.phone, localState.password)

      Toast({
        type: 'success',
        message: t('success_message_login'),
      })

      // Honour any stored target URL (e.g. user was bounced to login mid-flow).
      const target = getCookie('targetUrl')
      navigate(target ?? '/')
    } catch {
      Toast({
        type: 'error',
        message: t('error_message_login'),
      })
      setErrorMessage((prev) => ({
        ...prev,
        phone: t('incorrect_login_or_password'),
        password: t('incorrect_login_or_password'),
      }))
    } finally {
      setIsPending(false)
    }
  }

  return (
    <SigninWrapper>
      <SigninWrapper.Header>
        <SigninWrapper.HeaderTitle>{t('login')}</SigninWrapper.HeaderTitle>
        <SigninWrapper.HeaderDescription>{t('login_description')}</SigninWrapper.HeaderDescription>
      </SigninWrapper.Header>
      {/* Wrapping fields + submit button in a real <form> so password
          managers and the browser's autofill engine treat the username
          + current-password pair as one credential surface. noValidate
          skips browser-default required-field UI since we render our own. */}
      <form
        name='signin'
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
        noValidate
        autoComplete='on'
      >
        <SigninWrapper.Body>
          <Input
            name='phone'
            label={t('login_modal_phone_label')}
            value={localState.phone}
            onChange={handleChange}
            onPressEnter={handleSubmit}
            suffix={errorMessage?.phone ? <ExclamationCircleIcon color='red' /> : ''}
            status={errorMessage?.phone ? 'error' : undefined}
            errorData={errorMessage}
            field='phone'
            autoComplete='username'
            prefix={
              <span className='flex items-center gap-1.25'>
                <LoginIcon /> +998
              </span>
            }
          />
          <InputPassword
            name='password'
            value={localState.password}
            placeholder={t('password')}
            aria-label={t('password')}
            onChange={handleChange}
            onPressEnter={handleSubmit}
            suffix={errorMessage?.password ? <ExclamationCircleIcon color='red' /> : ''}
            status={errorMessage?.password ? 'error' : undefined}
            errorData={errorMessage}
            field='password'
            mode='current'
            prefix={<PasswordIcon />}
          />
        </SigninWrapper.Body>
        <SigninWrapper.Footer>
          <Button
            color='#fff'
            type='primary'
            htmlType='submit'
            size='medium'
            width='140px'
            borderRadius='1px'
            background={'var(--primary-background-color)'}
            border={'var(--primary-background-color)'}
            loading={isPending}
            disabled={isPending}
          >
            {t('continue')}
          </Button>
        </SigninWrapper.Footer>
      </form>
    </SigninWrapper>
  )
}
